import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Testimonials from '../components/ui/testimonials';
import { ThemeLogo } from '../components/ui/theme-logo';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/use-toast';
import { isPWAStandalone, getPWAInfo } from '../lib/utils';

import { Spotlight } from '../components/ui/spotlight';
import { 
  ArrowRight, 
  Sparkles, 
  RefrigeratorIcon, 
  Calendar, 
  ShoppingCart,
  Award,
  Zap,
  Camera,
  Bot,
  Heart,
  Receipt,
  UtensilsCrossed,
  Flame,
  Scan,
  ChevronRight,
  Instagram,
  Twitter,
  Facebook,
  Github,
  Mail,
  Star,
  Check,
  Clock
} from 'lucide-react';

const NavLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a 
    href={href} 
    className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
  >
    {children}
  </a>
);

const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  color, 
  delay 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  color: string; 
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow"
  >
    <div className={`inline-flex p-3 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 text-${color}-600 dark:text-${color}-400 mb-4`}>
      {icon}
    </div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
    <p className="text-gray-600 dark:text-gray-400">{description}</p>
  </motion.div>
);

const StepCard = ({ 
  number, 
  title, 
  description, 
  icon, 
  delay 
}: { 
  number: string; 
  title: string; 
  description: string; 
  icon: React.ReactNode; 
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="text-center"
  >
    <div className="relative mb-6">
      <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white mx-auto mb-4">
        {icon}
      </div>
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-hover rounded-full flex items-center justify-center text-white text-sm font-bold">
        {number}
      </div>
    </div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
    <p className="text-gray-600 dark:text-gray-400">{description}</p>
  </motion.div>
);

const CommunityCard = ({ 
  title, 
  description, 
  icon, 
  color, 
  delay 
}: { 
  title: string; 
  description: string; 
  icon: React.ReactNode; 
  color: string; 
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow"
  >
    <div className={`inline-flex p-3 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 text-${color}-600 dark:text-${color}-400 mb-4`}>
      {icon}
    </div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
    <p className="text-gray-600 dark:text-gray-400">{description}</p>
  </motion.div>
);

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('inventory');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle OAuth errors from root URL redirects
  useEffect(() => {
    const handleOAuthErrors = () => {
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for OAuth cancellation at root URL
      if (error === 'access_denied' || 
          (error && errorDescription && errorDescription.toLowerCase().includes('cancel'))) {
        
        console.log('🚫 OAuth cancellation detected at root URL, redirecting to login page:', {
          error,
          errorDescription
        });

        // Show toast notification about cancellation
        toast({
          title: "Google sign-up cancelled",
          description: "You cancelled the Google sign-up process. Redirecting to login page...",
          variant: "default",
        });

        // Always redirect to login page for OAuth cancellations
        setTimeout(() => {
          console.log('🔧 Redirecting to login page after OAuth cancellation');
          navigate('/auth/login');
        }, 1500);
        return;
      }

      // Handle any other OAuth errors at root level
      if (error) {
        console.log('🚫 OAuth error detected at root URL:', {
          error,
          errorDescription
        });

        toast({
          title: "Authentication Error",
          description: errorDescription || "There was an issue with the authentication process. Please try again.",
          variant: "destructive",
        });

        // Clean up URL parameters and redirect to login
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
        return;
      }
    };

    handleOAuthErrors();
  }, [searchParams, navigate, toast]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Detect PWA context for better user experience
          const isPWA = isPWAStandalone();
          const pwaInfo = getPWAInfo();
          
          console.log('✅ User is authenticated, redirecting to dashboard...', {
            isPWA,
            currentPath: window.location.pathname,
            ...pwaInfo
          });
          
          // Special handling for PWA installations and root URL access: 
          // If user is authenticated and on root URL, redirect to dashboard
          // This ensures PWA opens directly to dashboard for logged-in users
          if (window.location.pathname === '/') {
            if (isPWA) {
              console.log('📱 PWA mode detected, redirecting authenticated user to dashboard');
            } else {
              console.log('🏠 Direct root access detected, redirecting authenticated user to dashboard');
            }
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/dashboard');
          }
          return;
        }
        
        console.log('ℹ️ User is not authenticated, staying on landing page');
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate, toast]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show loading spinner while checking authentication
  if (isCheckingAuth) {
    const isPWA = isPWAStandalone();
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {isPWA ? 'Loading your dashboard...' : 'Checking authentication...'}
          </p>
          {isPWA && (
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              📱 PWA Mode - Welcome back!
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 shadow-md py-3' 
          : 'bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <ThemeLogo className="h-[42px]" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <NavLink href="#features">Features</NavLink>
              <NavLink href="#how-it-works">How It Works</NavLink>
              <NavLink href="#community">Community</NavLink>
              <NavLink href="#pricing">Pricing</NavLink>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth/login">
                <Button variant="ghost" className="hidden sm:flex">Sign in</Button>
              </Link>
              <Link to="/auth/login">
                <Button className="bg-primary hover:bg-primary-hover text-white">
                  Get Started
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Bolt.new Badge */}
        <motion.a
          href="https://bolt.new/"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          whileHover={{ scale: 1.05 }}
          className="absolute top-24 right-4 sm:top-20 sm:right-6 md:top-24 md:right-8 lg:top-28 lg:right-10 z-30 group"
        >
          <img
            src="/black_circle_360x360.png"
            alt="Powered by Bolt.new"
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-20 md:h-20 lg:w-22 lg:h-22 transition-all duration-300 group-hover:brightness-110 group-hover:drop-shadow-lg"
          />
        </motion.a>
        
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center md:text-left"
              >
                <div className="inline-block px-3 py-1 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <span className="flex items-center">
                    <Sparkles size={16} className="mr-2" />
                    AI-Powered Food Management
                  </span>
                </div>
                
                <div className="mb-6">
                  <img 
                    src="/slogan.png" 
                    alt="Reduce Food Waste with AI-Powered Inventory. Know What You Have. Cook What You Love." 
                    className="w-full max-w-2xl mx-auto md:mx-0"
                  />
                </div>
                
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto md:mx-0">
                Snap, Track, Cook & Explore. Get AI-powered recipe ideas based on what you already have in the kitchen. Track personalized calories, log nutrients from a meal photo, and get notified before your ingredients expire.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <Link to="/auth/login">
                    <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white">
                      Get Started Free
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    <UtensilsCrossed size={18} className="mr-2" />
                    Watch Demo
                  </Button>
                </div>
                
                <div className="mt-8 flex items-center justify-center md:justify-start">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 overflow-hidden">
                        <img 
                          src={`/img/avatars/avatar-${i}.png`} 
                          alt={`User ${i}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">4.9/5</span> from 2,000+ users
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative z-10 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
                  <div className="absolute top-0 left-0 right-0 h-12 bg-gray-50 dark:bg-gray-700 flex items-center px-4">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  
                  <div className="pt-12 pb-4 px-4">
                    <div className="flex space-x-4 mb-4">
                      <button 
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          activeTab === 'inventory' 
                            ? 'bg-primary text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => setActiveTab('inventory')}
                      >
                        Inventory
                      </button>
                      <button 
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          activeTab === 'recipes' 
                            ? 'bg-primary text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => setActiveTab('recipes')}
                      >
                        AI Recipes
                      </button>
                      <button 
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          activeTab === 'tracker' 
                            ? 'bg-primary text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => setActiveTab('tracker')}
                      >
                        Tracker
                      </button>
                    </div>
                    
                    {activeTab === 'inventory' && (
                      <div className="space-y-3 min-h-[320px]">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-semibold">My Inventory</h3>
                          <div className="flex space-x-2">
                            <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <Camera size={16} />
                            </button>
                            <button className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <Receipt size={16} />
                            </button>
                          </div>
                        </div>
                        
                        {[
                          { name: 'Chicken Breast', emoji: '🍗', expiry: '2 days', location: 'fridge' },
                          { name: 'Spinach', emoji: '🥬', expiry: '3 days', location: 'fridge' },
                          { name: 'Tomatoes', emoji: '🍅', expiry: '4 days', location: 'fridge' },
                          { name: 'Brown Rice', emoji: '🍚', expiry: '120 days', location: 'pantry' },
                        ].map((item, i) => (
                          <motion.div 
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * i }}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-xl"
                          >
                            <div className="flex items-center">
                              <span className="text-xl mr-3">{item.emoji}</span>
                              <div>
                                <p className="font-medium text-sm">{item.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Expires in {item.expiry}</p>
                              </div>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {item.location}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                    
                    {activeTab === 'recipes' && (
                      <div className="space-y-3 min-h-[320px]">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-semibold">AI Recipe Suggestions</h3>
                          <button className="p-2 bg-primary/10 text-primary rounded-lg flex items-center text-xs">
                            <Sparkles size={14} className="mr-1" />
                            Generate
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { 
                              name: 'Chicken Stir Fry', 
                              time: '25m', 
                              cal: '420', 
                              img: '/img/small 1.png',
                              nutrition: { protein: 28, carbs: 15, fat: 22 },
                              heroIngredients: [
                                { name: 'Chicken', emoji: '🍗', category: 'poultry' },
                                { name: 'Broccoli', emoji: '🥦', category: 'vegetables' },
                                { name: 'Rice', emoji: '🍚', category: 'grains' }
                              ]
                            },
                            { 
                              name: 'Spinach Salad', 
                              time: '15m', 
                              cal: '280', 
                              img: '/img/small 2.png',
                              nutrition: { protein: 18, carbs: 12, fat: 16 },
                              heroIngredients: [
                                { name: 'Spinach', emoji: '🥬', category: 'vegetables' },
                                { name: 'Chicken', emoji: '🍗', category: 'poultry' },
                                { name: 'Avocado', emoji: '🥑', category: 'fruits' }
                              ]
                            },
                          ].map((recipe, i) => (
                            <motion.div 
                              key={i}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2 * i }}
                              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden cursor-pointer"
                            >
                              {/* Large food image - no shadow, much bigger like reference */}
                              <div className="w-full h-40 overflow-hidden">
                                <img
                                  src={recipe.img}
                                  alt={recipe.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              
                              {/* Content below image - compact layout */}
                              <div className="p-3">
                                <div className="text-center">
                                  <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-1 mb-2">
                                    {recipe.name}
                                  </h3>
                                  
                                  {/* Hero Ingredients - Smaller and more compact */}
                                  <div className="mb-2">
                                    <div className="flex items-center justify-center gap-1">
                                      {recipe.heroIngredients.slice(0, 2).map((ingredient, index) => {
                                        const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
                                          'poultry': { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-800/30' },
                                          'meat': { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-800/30' },
                                          'fish': { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-800/30' },
                                          'grains': { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-800/30' },
                                          'vegetables': { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-800/30' },
                                          'fruits': { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-800/30' },
                                          'dairy': { bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-800/30' },
                                          'default': { bg: 'bg-gray-50 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-800/30' }
                                        };
                                        const colors = categoryColors[ingredient.category] || categoryColors.default;
                                        return (
                                          <div key={index} className={`flex items-center ${colors.bg} rounded-full px-2 py-0.5 border dark:${colors.border}`}>
                                            <span className="text-xs mr-1">{ingredient.emoji}</span>
                                            <span className={`text-xs font-medium ${colors.text}`}>{ingredient.name}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  
                                  <div className="flex justify-center gap-3 text-gray-400 dark:text-gray-500 text-xs mb-2">
                                    <div className="flex items-center">
                                      <Clock size={10} className="mr-1" />
                                      <span>{recipe.time}</span>
                                    </div>
                                    
                                    <div className="flex items-center">
                                      <Flame size={10} className="mr-1" />
                                      <span>{recipe.cal}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Compact Nutrition Bars - Much smaller to save space */}
                                  <div className="space-y-1">
                                    {/* Protein */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1"></div>
                                        <span className="text-gray-600 dark:text-gray-400 text-xs">Protein</span>
                                      </div>
                                      <div className="flex items-center ml-1">
                                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-8 h-1">
                                          <motion.div
                                            className="h-full bg-blue-500 rounded-full"
                                            initial={{ width: "0%" }}
                                            animate={{ width: `${Math.min((recipe.nutrition.protein / 30) * 100, 100)}%` }}
                                            transition={{ duration: 1.2, delay: 0.2 + (i * 0.1), ease: "easeOut" }}
                                          />
                                        </div>
                                        <span className="ml-1 font-medium text-gray-700 dark:text-gray-300 text-xs">
                                          {recipe.nutrition.protein}g
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Carbs */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></div>
                                        <span className="text-gray-600 dark:text-gray-400 text-xs">Carbs</span>
                                      </div>
                                      <div className="flex items-center ml-1">
                                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-8 h-1">
                                          <motion.div
                                            className="h-full bg-green-500 rounded-full"
                                            initial={{ width: "0%" }}
                                            animate={{ width: `${Math.min((recipe.nutrition.carbs / 20) * 100, 100)}%` }}
                                            transition={{ duration: 1.2, delay: 0.5 + (i * 0.1), ease: "easeOut" }}
                                          />
                                        </div>
                                        <span className="ml-1 font-medium text-gray-700 dark:text-gray-300 text-xs">
                                          {recipe.nutrition.carbs}g
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Fat */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-1"></div>
                                        <span className="text-gray-600 dark:text-gray-400 text-xs">Fat</span>
                                      </div>
                                      <div className="flex items-center ml-1">
                                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-8 h-1">
                                          <motion.div
                                            className="h-full bg-orange-500 rounded-full"
                                            initial={{ width: "0%" }}
                                            animate={{ width: `${Math.min((recipe.nutrition.fat / 25) * 100, 100)}%` }}
                                            transition={{ duration: 1.2, delay: 0.8 + (i * 0.1), ease: "easeOut" }}
                                          />
                                        </div>
                                        <span className="ml-1 font-medium text-gray-700 dark:text-gray-300 text-xs">
                                          {recipe.nutrition.fat}g
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'tracker' && (
                      <div className="space-y-3 min-h-[320px]">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-semibold">Meal Tracker</h3>
                          <button className="p-2 bg-primary/10 text-primary rounded-lg flex items-center text-xs">
                            <Camera size={14} className="mr-1" />
                            Scan Meal
                          </button>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium">Today's Calories</p>
                            <p className="text-sm font-medium">1,240 / 2,100</p>
                          </div>
                          <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: '59%' }}></div>
                          </div>
                        </div>
                        
                        {[
                          { name: 'Breakfast', food: 'Avocado Toast', cal: '320', time: '8:30 AM' },
                          { name: 'Lunch', food: 'Chicken Salad', cal: '450', time: '12:45 PM' },
                          { name: 'Snack', food: 'Greek Yogurt', cal: '150', time: '3:30 PM' },
                        ].map((meal, i) => (
                          <motion.div 
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * i }}
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700"
                          >
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{meal.name} • {meal.time}</p>
                              <p className="font-medium text-sm">{meal.food}</p>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                              {meal.cal} cal
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        
        {/* Trusted by section */}
        <div className="max-w-7xl mx-auto mt-16">
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Trusted by eco-conscious users worldwide</p>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {['🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷'].map((flag, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-4xl"
              >
                {flag}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading-rounded font-bold text-gray-900 dark:text-white mb-4">
                Smart Features for Better Food Management
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Everything you need to manage your food inventory efficiently and reduce waste
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<RefrigeratorIcon size={24} />}
              title="Smart Inventory Tracking"
              description="Know what's in your fridge, pantry, and freezer at a glance with expiration alerts"
              color="blue"
              delay={0.1}
            />
            <FeatureCard 
              icon={<Bot size={24} />}
              title="AI Recipe Suggestions"
              description="Get personalized recipe suggestions based on your available ingredients and dietary preferences"
              color="purple"
              delay={0.2}
            />
            <FeatureCard 
              icon={<Camera size={24} />}
              title="Fridge & Pantry Scanning"
              description="Snap a photo — we'll detect and log everything inside automatically with AI vision"
              color="green"
              delay={0.3}
            />
            <FeatureCard 
              icon={<Receipt size={24} />}
              title="Receipt Scanning"
              description="Scan grocery receipts to add all your purchases in one go with automatic categorization"
              color="amber"
              delay={0.4}
            />
            <FeatureCard 
              icon={<UtensilsCrossed size={24} />}
              title="Meal Scanning & Tracking"
              description="Take a picture of your meal and get instant calorie and macronutrient estimates"
              color="red"
              delay={0.5}
            />
            <FeatureCard 
              icon={<Calendar size={24} />}
              title="Weekly Meal Planning"
              description="Plan your meals for the week and generate smart shopping lists based on what you need"
              color="indigo"
              delay={0.6}
            />
            <FeatureCard 
              icon={<Flame size={24} />}
              title="Diet Preferences & Filters"
              description="Supports vegetarian, vegan, keto, halal, gluten-free and more — your meals, your way"
              color="orange"
              delay={0.7}
            />
            <FeatureCard 
              icon={<Award size={24} />}
              title="Gamified Experience"
              description="Earn points and achievements while reducing food waste and tracking your impact"
              color="emerald"
              delay={0.8}
            />
            <FeatureCard 
              icon={<Heart size={24} />}
              title="Full Calorie Tracker"
              description="Track calories and all macronutrients with a comprehensive nutrition database"
              color="pink"
              delay={0.9}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading-rounded font-bold text-gray-900 dark:text-white mb-4">
                How Nosh Works
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Start reducing food waste in three simple steps
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard 
              number="1"
              title="Track Your Food"
              description="Add items to your inventory by scanning or manual entry"
              icon={<Scan size={32} />}
              delay={0.1}
            />
            <StepCard 
              number="2"
              title="Get Smart Suggestions"
              description="Receive AI-powered recipe suggestions based on your inventory"
              icon={<Sparkles size={32} />}
              delay={0.3}
            />
            <StepCard 
              number="3"
              title="Reduce Waste"
              description="Use items before they expire and track your impact"
              icon={<Zap size={32} />}
              delay={0.5}
            />
          </div>
          
          {/* App Screenshots */}
          <div className="mt-20">
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700"
              >
                <img 
                  src="https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg"
                  alt="Nosh App Interface" 
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay with feature callouts */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h3 className="text-2xl sm:text-3xl font-bold mb-4">Powerful AI Features</h3>
                    <p className="text-lg max-w-lg mx-auto mb-8">Scan, track, and get personalized recommendations</p>
                    <Link to="/auth/login">
                      <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                        Try It Now
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
              
              {/* Feature callouts */}
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute top-1/4 left-0 md:left-10 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700 max-w-xs hidden md:block"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg text-blue-600 dark:text-blue-400">
                    <Camera size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Instant Scanning</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Scan your fridge or receipts in seconds</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="absolute bottom-1/4 right-0 md:right-10 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700 max-w-xs hidden md:block"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg text-purple-600 dark:text-purple-400">
                    <Bot size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">AI Recipe Generation</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Get personalized recipes from your inventory</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading-rounded font-bold text-gray-900 dark:text-white mb-4">
                Join Our Community
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Connect with other food-conscious people, share recipes, and participate in challenges
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <CommunityCard 
              title="Share & Discover Recipes"
              description="Share your creations and discover new recipes from the community"
              icon={<UtensilsCrossed size={24} />}
              color="amber"
              delay={0.1}
            />
            <CommunityCard 
              title="Weekly Challenges"
              description="Participate in weekly food waste reduction challenges and earn badges"
              icon={<Award size={24} />}
              color="emerald"
              delay={0.3}
            />
            <CommunityCard 
              title="Meal Creation Challenge"
              description="Create meals from specific ingredients and vote for the best recipes"
              icon={<Flame size={24} />}
              color="red"
              delay={0.5}
            />
          </div>
          
          {/* Community Testimonials */}
          <div className="mt-20">
            <Testimonials />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading-rounded font-bold text-gray-900 dark:text-white mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Start free and upgrade when you're ready for more features
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Free</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">$0</span>
                  <span className="text-gray-600 dark:text-gray-400">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Track up to 50 food items</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">10 recipe generations/month</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">10 meal logs/month</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Manual inventory entry only</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Basic meal planner</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Basic shopping list</span>
                  </li>
                </ul>
                <Link to="/auth/login">
                  <Button variant="outline" className="w-full">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Pro Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-primary text-white rounded-2xl p-8 shadow-xl border-2 border-primary relative"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-hover text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Pro</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$15</span>
                  <span className="text-primary-100">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>Full-featured inventory tracking</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>Unlimited photo/receipt scanning</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>AI recipe generation (200/month)</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>Unlimited meal logs</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>Full-featured meal planning</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-white mr-3" />
                    <span>Personalized calorie tracker</span>
                  </li>
                </ul>
                <Link to="/auth/login">
                  <Button variant="secondary" className="w-full bg-white text-primary hover:bg-gray-100">
                    Start Pro Trial
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Premium Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Premium</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">$19</span>
                  <span className="text-gray-600 dark:text-gray-400">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Everything in Pro</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Family sharing (up to 6 members)</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Advanced analytics</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Custom dietary preferences</span>
                  </li>
                  <li className="flex items-center">
                    <Check size={16} className="text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Priority support</span>
                  </li>
                </ul>
                <Link to="/auth/login">
                  <Button variant="outline" className="w-full">
                    Start Premium Trial
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="/nosh-new-wh.png" alt="Nosh" className="h-[42px]" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Reduce food waste with AI-powered inventory management, recipe suggestions, and meal tracking. 
                <br />
                <span className="block mt-2">
                  Database powered by Supabase.
                </span>
                <span className="block mt-1">
                  Org slug: ursweylcirmkikguixbp
                </span>
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github size={20} />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/help-center" 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/privacy-policy" 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/terms-of-service" 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Nosh. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Mail size={16} className="mr-2 text-gray-400" />
              <a href="mailto:hello@noshhq.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                hello@noshhq.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;